/**
 * Los seis números de una campaña o de un período, y el embudo del envío a la respuesta.
 *
 * Las bases van dichas al lado de cada tasa (como en Estadísticas de WhatsApp): «les llegó» y los rebotes sobre las
 * personas a las que se les mandó; aperturas, clics, respuestas y bajas sobre las personas a las que les llegó.
 * Aperturas y clics sin medir (apagados en Snov) son «sin medir», nunca 0; si sólo algunas campañas los miden, se dice.
 *
 * En varias campañas los números suman campañas (quien recibió dos cuenta dos veces); `personas` es a cuántas
 * DISTINTAS se llegó, y va en el primero.
 */

import { CheckCheck, Eye, MessageSquareReply, MousePointerClick, Send, UserMinus } from "lucide-react";
import StatTile from "../../campana-nueva/components/StatTile";
import { num, plural } from "../../campana-nueva/utils/format";
import type { Conteos, ConteosSumados, Tasas } from "../types";
import { metro, tasaTexto } from "../logic/emails";

const esSumados = (c: Conteos | ConteosSumados): c is ConteosSumados => "baseAperturas" in c;

export function Indicadores({ conteos, tasas, personas }: { conteos: Conteos | ConteosSumados; tasas: Tasas; personas?: number }) {
  const c = conteos;
  const sinAperturas = esSumados(c) ? c.campanasSinAperturas : c.abrieron === null ? 1 : 0;
  const sinClics = esSumados(c) ? c.campanasSinClics : c.hicieronClic === null ? 1 : 0;
  const parcialAperturas = esSumados(c) && c.abrieron !== null && sinAperturas > 0;
  const parcialClics = esSumados(c) && c.hicieronClic !== null && sinClics > 0;
  const chipParcial = { texto: "parcial", cls: "bg-amber-50 text-amber-700" };
  // En varias campañas, a cuántas personas distintas; en una, son las mismas que «se les mandó».
  const alcance = personas ?? c.contactados;
  const repetidas = esSumados(c) && personas !== undefined && personas < c.contactados;
  const bajas = c.bajas + (c.pidieronBaja || 0);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatTile icon={<Send size={15} />} label="Mails enviados" value={num(c.enviados)}
          sub={`a ${num(alcance)} ${plural(alcance, "persona", "personas")}${repetidas ? " distintas" : ""}`} />
        <StatTile icon={<CheckCheck size={15} />} label="Les llegó" value={num(c.entregados)} tono="entrega" meterPct={metro(tasas.entrega)}
          sub={`${tasaTexto(tasas.entrega)} de a quienes se les mandó · ${num(c.rebotes)} ${plural(c.rebotes, "rebotó", "rebotaron")}`} />
        <StatTile icon={<Eye size={15} />} label="Abrieron" value={c.abrieron === null ? null : num(c.abrieron)} tono="lectura" meterPct={metro(tasas.apertura)}
          sub={parcialAperturas ? `${tasaTexto(tasas.apertura)} en las campañas que lo miden` : `${tasaTexto(tasas.apertura)} de a quienes les llegó`}
          chip={parcialAperturas ? chipParcial : undefined}
          vacio="no se mide (apagado en Snov)" />
        <StatTile icon={<MousePointerClick size={15} />} label="Hicieron clic" value={c.hicieronClic === null ? null : num(c.hicieronClic)} tono="resultado" meterPct={metro(tasas.clic)}
          sub={parcialClics ? `${tasaTexto(tasas.clic)} en las campañas que lo miden` : `${tasaTexto(tasas.clic)} de a quienes les llegó`}
          chip={parcialClics ? chipParcial : undefined}
          vacio="no se mide (apagado en Snov)" />
        <StatTile icon={<MessageSquareReply size={15} />} label="Respondieron" value={num(c.respondieron)} tono="respuesta" meterPct={metro(tasas.respuesta)}
          sub={`${tasaTexto(tasas.respuesta)} de a quienes les llegó${c.autorespuestas ? ` · más ${num(c.autorespuestas)} ${plural(c.autorespuestas, "automática", "automáticas")}` : ""}`} />
        <StatTile icon={<UserMinus size={15} />} label="Pidieron la baja" value={num(bajas)} tono="baja"
          sub={`${tasaTexto(tasas.baja)} de a quienes les llegó${c.pidieronBaja ? ` · ${num(c.bajas)} con el link y ${num(c.pidieronBaja)} contestando` : ""}`} />
      </div>
      {repetidas && (
        <p className="text-[11px] text-slate-400">Salvo las personas distintas, los números suman las campañas: quien recibió dos cuenta dos veces.</p>
      )}
    </div>
  );
}

/**
 * Del envío a la respuesta de UNA campaña: cada barra sobre las personas a las que se les mandó, para que se lea de un
 * vistazo cuánto se pierde en cada paso. Los pasos sin medir no se dibujan (y se dice). En el resumen de varias
 * campañas no va: si sólo algunas miden aperturas, la barra de «Abrieron» quedaría sobre otra base.
 */
export function Embudo({ conteos }: { conteos: Conteos }) {
  const base = conteos.contactados;
  const pasos: { label: string; n: number | null }[] = [
    { label: "Se les mandó", n: conteos.contactados },
    { label: "Les llegó", n: conteos.entregados },
    { label: "Abrieron", n: conteos.abrieron },
    { label: "Hicieron clic", n: conteos.hicieronClic },
    { label: "Respondieron", n: conteos.respondieron },
  ];
  const sinMedir = pasos.filter((p) => p.n === null).map((p) => p.label.toLowerCase());
  return (
    <div className="space-y-2.5">
      {pasos.filter((p) => p.n !== null).map((p) => {
        const pct = base > 0 ? ((p.n as number) / base) * 100 : 0;
        return (
          <div key={p.label}>
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="text-slate-600">{p.label}</span>
              <span className="font-semibold text-slate-900 tabular-nums">
                {num(p.n as number)} <span className="text-xs font-normal text-slate-400">{tasaTexto(base > 0 ? (p.n as number) / base : null)}</span>
              </span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-violet-50 overflow-hidden" aria-hidden="true">
              <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.max(pct > 0 ? 1 : 0, Math.min(100, pct))}%` }} />
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-slate-400">
        Cada paso, sobre las personas a las que se les mandó.{sinMedir.length ? ` Sin medir: ${sinMedir.join(" y ")} (apagado en Snov).` : ""}
      </p>
    </div>
  );
}
